import React from 'react'
import useSWRMutation from 'swr/mutation'
import { useNavigate } from 'react-router-dom'
import useSWR from 'swr'

import Input from '../ui/input'
import Button, { ButtonSize } from '../ui/button'
import { useAuth } from '../../context/auth'
import { useAlerts } from '../../context/alerts'
import { getDefaultCurrency, updateBusinessDetails, updateUnsentInvoicesTax } from '../../api'
import { getDefaultCurrencyRequestUrl } from '../../lib/defaultCurrencyUrl'
import { resolveBusinessCurrencyCode } from '../../lib/visitorCurrency'
import TextArea from '../ui/input/textArea'
import * as utils from '../../utils'
import Checkbox from '../ui/input/checkbox'
import CurrencySelector from '../invoices/currencySelector'
import BusinessLogoEditor from './logoEditor'
import TaxUpdateScopeModal from '../invoices/taxUpdateScopeModal'

/**
 * Form to create/edit business details of user.
 */
type Props = {
  autofocusField?: string // name of field to autofocus
  redirectAfterSaveTo?: string // path to redirect after save
  saveLabel?: string
  silent?: boolean // if true no alert is shown after save
}
const BusinessDetailsForm: React.FC<Props> = ({ silent, autofocusField, redirectAfterSaveTo, saveLabel = 'Save' }) => {
  const navigate = useNavigate()
  const { user, mutate } = useAuth()
  const [values, setValues] = React.useState({
    'businessDetails.name': '',
    'businessDetails.email': user?.businessDetails?.email || user?.email || '',
    'businessDetails.billingAddress': '',
    'businessDetails.taxRegistrationNumber': '',
    'businessDetails.paymentInstructions': '',
    'businessDetails.tax.name': '',
    'businessDetails.tax.percentage': '15',
    'businessDetails.enableTax': '',
    'businessDetails.currency.code': resolveBusinessCurrencyCode({
      savedCode: user?.businessDetails?.currency?.code,
    }),
    'businessDetails.logoSrc': user?.businessDetails?.logoSrc,
  })
  const { trigger, isMutating } = useSWRMutation('/api/user/business', updateBusinessDetails)
  const { trigger: triggerUpdateUnsentInvoicesTax, isMutating: isUpdatingUnsentInvoicesTax } = useSWRMutation(
    '/api/invoices/tax/unsent',
    updateUnsentInvoicesTax
  )
  const defaultCurrencyUrl = getDefaultCurrencyRequestUrl()
  const { data: getDefaultCurrencyData } = useSWR(defaultCurrencyUrl, getDefaultCurrency)
  const { addAlert } = useAlerts()
  const [pristine, setPristine] = React.useState(true)
  const [showTaxUpdateScopeModal, setShowTaxUpdateScopeModal] = React.useState(false)
  const [pendingTaxPayload, setPendingTaxPayload] = React.useState<{ enableTax: boolean, tax: { name: string, percentage: string | number } } | null>(null)

  React.useEffect(() => {
    if(user && user.businessDetails) {
      const formattedData = utils.flattenObject(user)
      // Always keep this key so PATCH sends "" when cleared (flatten omits missing DB fields).
      formattedData['businessDetails.taxRegistrationNumber'] =
        user.businessDetails?.taxRegistrationNumber ?? ''
      formattedData['businessDetails.currency.code'] = resolveBusinessCurrencyCode({
        savedCode: user.businessDetails?.currency?.code,
        defaultCode: getDefaultCurrencyData?.code,
      })
      setValues(formattedData)
      setPristine(true)
    } else if (!user?.businessDetails?.currency?.code) {
      setValues((v) => ({
        ...v,
        'businessDetails.currency.code': resolveBusinessCurrencyCode({
          savedCode: user?.businessDetails?.currency?.code,
          defaultCode: getDefaultCurrencyData?.code,
        }),
      }))
    }
  }, [user, getDefaultCurrencyData])

  const getTaxSnapshot = (source: any) => ({
    enableTax: !!source?.['businessDetails.enableTax'],
    name: String(source?.['businessDetails.tax.name'] ?? '').trim(),
    percentage: String(source?.['businessDetails.tax.percentage'] ?? '').trim(),
  })

  const getTaxSnapshotFromUser = () => ({
    enableTax: !!user?.businessDetails?.enableTax,
    name: String(user?.businessDetails?.tax?.name ?? '').trim(),
    percentage: String(user?.businessDetails?.tax?.percentage ?? '').trim(),
  })

  const hasTaxChanged = () => {
    const before = getTaxSnapshotFromUser()
    const after = getTaxSnapshot(values)
    return (
      before.enableTax !== after.enableTax
      || before.name !== after.name
      || before.percentage !== after.percentage
    )
  }

  const finishAfterSave = () => {
    if (redirectAfterSaveTo) {
      return navigate(redirectAfterSaveTo)
    }
    return navigate('/profile')
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    try {
      await trigger({ body: values })
      await mutate()
      setPristine(true)
      if(!silent) {
        addAlert({ type: 'info', message: 'Your details have been updated' })
      }

      if (hasTaxChanged()) {
        setPendingTaxPayload({
          enableTax: !!values['businessDetails.enableTax'],
          tax: {
            name: String(values['businessDetails.tax.name'] ?? ''),
            percentage: values['businessDetails.tax.percentage'],
          },
        })
        setShowTaxUpdateScopeModal(true)
        return
      }

      return finishAfterSave()
    } catch(error) {
      console.log('error', error)
    }
  }

  const skipTaxBulkUpdate = () => {
    setShowTaxUpdateScopeModal(false)
    setPendingTaxPayload(null)
    finishAfterSave()
  }

  const applyTaxToUnsentInvoices = async () => {
    if (!pendingTaxPayload) return
    try {
      const response = await triggerUpdateUnsentInvoicesTax({ body: pendingTaxPayload })
      addAlert({
        type: 'info',
        message: `Updated tax settings in ${response?.data?.modifiedCount ?? 0} unsent invoices`,
      })
    } catch (_error) {
      addAlert({ type: 'error', message: 'Could not update unsent invoices tax settings' })
    } finally {
      setShowTaxUpdateScopeModal(false)
      setPendingTaxPayload(null)
      finishAfterSave()
    }
  }

  const handleTextChange = (e: any) => {
    const { name, value } = e.target
    setValues({ ...values, [name]: value })
    setPristine(false)
  }

  const handleToggleTax = (e: any) => {
    const { name, checked } = e.target
    setValues({ ...values, [name]: checked })
    setPristine(false)
  }

  return (
    <div className='row'>
      <div className='col'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-12 col-md-9'>
              <div className='mb-4'>
                <Input
                  label={`Your business name`}
                  autoFocus={autofocusField === 'businessDetails.name'}
                  id='businessDetails.name'
                  defaultValue={values['businessDetails.name']}
                  name='businessDetails.name'
                  onChange={handleTextChange}
                  required
                  tooltip='This will be shown on your invoices and emails sent to your clients.'
                  maxLength={150}
                />
              </div>

              <div className='mb-4'>
                <Input
                  label={`Your business email`}
                  type='email'
                  id='businessDetails.email'
                  name='businessDetails.email'
                  defaultValue={values['businessDetails.email']}
                  onChange={handleTextChange}
                  required
                  tooltip='Your clients will be able to email you at this address.'
                  maxLength={150}
                />
              </div>
            </div>
            <div className='mb-4 mb-md-0 col-12 col-md-3 col-lg-2 mx-auto business-logo-col--mobile'>
              <BusinessLogoEditor imageSrc={user?.businessDetails?.logoSrc} compact />
            </div>
          </div>
          <div className='mb-4'>
            <CurrencySelector onChange={handleTextChange} value={values['businessDetails.currency.code']} />
          </div>
          <div className='mb-4'>
            <Input
              label={`Billing Address`}
              id='businessDetails.billingAddress'
              name='businessDetails.billingAddress'
              defaultValue={values['businessDetails.billingAddress']}
              onChange={handleTextChange}
              maxLength={250}
              optional
            />
          </div>
          <div className='mb-4'>
            <Input
              label='Tax Registration #'
              id='businessDetails.taxRegistrationNumber'
              name='businessDetails.taxRegistrationNumber'
              value={values['businessDetails.taxRegistrationNumber'] ?? ''}
              onChange={handleTextChange}
              maxLength={80}
              optional
              tooltip='Your business Tax Registration Number, it will be shown in all your invoices.'
            />
          </div>
          <div className='mb-2'>
            <Checkbox
              label={`Add Tax To Invoices`}
              id='businessDetails.enableTax'
              name='businessDetails.enableTax'
              defaultValue={values['businessDetails.enableTax']}
              onChange={handleToggleTax}
              checked={values['businessDetails.enableTax']}
            />
          </div>
          {values['businessDetails.enableTax'] && (
            <div className='row mb-4'>
              <div className='col-4'>
                <Input
                  label={`Tax Name`}
                  placeholder={`IVA, VAT, GST`}
                  id='businessDetails.tax.name'
                  name='businessDetails.tax.name'
                  defaultValue={values['businessDetails.tax.name']}
                  onChange={handleTextChange}
                  optional
                />
              </div>
              <div className='col-3'>
                <Input
                  label='Tax %'
                  type='number'
                  id='businessDetails.tax.percentage'
                  name='businessDetails.tax.percentage'
                  defaultValue={values['businessDetails.tax.percentage']}
                  onChange={handleTextChange}
                  optional
                />
              </div>
            </div>
          )}
          <div className='mb-4'>
            <TextArea
              rows={4}
              label='Payment Instructions'
              placeholder='Please deposit full amount to bank account: xxx-xxx-xxx-xxxx'
              id='businessDetails.paymentInstructions'
              name='businessDetails.paymentInstructions'
              defaultValue={values['businessDetails.paymentInstructions']}
              onChange={handleTextChange}
              optional
              tooltip='Describe how your clients should pay you.'
              maxLength={350}
            />
          </div>
          <div className='d-flex justify-content-between pt-4'>
            <Button size={ButtonSize.LARGE} loading={isMutating} type='submit' disabled={pristine || isMutating}>
              {saveLabel}
            </Button>
          </div>
        </form>
        <TaxUpdateScopeModal
          open={showTaxUpdateScopeModal}
          onClose={skipTaxBulkUpdate}
          description='Tax has been applied to future invoices. Do you also want to update all existing invoices that have not been sent yet?'
          onConfirm={applyTaxToUnsentInvoices}
          isUpdating={isUpdatingUnsentInvoicesTax}
        />
      </div>
    </div>
  )
}

export default BusinessDetailsForm
